import React from 'react'
import { ApolloError } from '@apollo/client'

import { ReactComponent as WarningTriangle } from '@sketch/icons/warning-triangle-64'

import { ProjectFragment } from '@sketch/gql-types'

import { ErrorMessage } from '@sketch/components'
import ProjectRequestAccess from '../ProjectRequestAccess'
import { hasGraphQLErrorCode } from '@sketch/modules-common'

const PENDING_PROJECT_REQUEST = 'PENDING_REQUEST'
const REQUEST_ACCESS_REASON = [
  'RESTRICTED_TO_PROJECT_MEMBERS',
  PENDING_PROJECT_REQUEST,
]

interface ProjectErrorProps {
  projectId: ProjectFragment['identifier']
  error?: ApolloError
}

const COPY = {
  NOT_FOUND: {
    title: `Folder not available`,
    description: `The folder you’re looking for might have been deleted, or you don’t have the right permissions to view it.`,
  },
  GENERIC: {
    title: `Something went wrong while loading the folder`,
  },
}

export const ProjectNotFoundError = () => {
  return (
    <ErrorMessage
      icon={<WarningTriangle />}
      title={COPY.NOT_FOUND.title}
      description={COPY.NOT_FOUND.description}
    />
  )
}

const ProjectError: React.FCC<ProjectErrorProps> = ({ projectId, error }) => {
  if (error) {
    const forbiddenError = hasGraphQLErrorCode(error, 'FORBIDDEN')

    if (
      forbiddenError &&
      REQUEST_ACCESS_REASON.includes(forbiddenError.extensions?.reason)
    ) {
      return (
        <ProjectRequestAccess
          projectShortId={projectId!}
          hasPendingRequest={
            forbiddenError.extensions!.reason === PENDING_PROJECT_REQUEST
          }
        />
      )
    }

    if (hasGraphQLErrorCode(error, 'NOT_FOUND')) {
      return (
        <ErrorMessage
          icon={<WarningTriangle />}
          title={COPY.NOT_FOUND.title}
          description={COPY.NOT_FOUND.description}
        />
      )
    }
  }

  return (
    <ErrorMessage.Generic
      icon={<WarningTriangle />}
      title={COPY.GENERIC.title}
    />
  )
}

export default ProjectError
